import React, { MouseEventHandler } from 'react';
import { FixedSizeList } from 'react-window';

import {
    Checkbox,
    ListItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
} from '@material-ui/core';
import { IBaseTransferListObject } from '../CustomList';
import TranslationHelper from '../../../../../helpers/TranslationHelper';
import { useStyles } from '../../../Themable.hooks';

interface IOwnProps<T extends IBaseTransferListObject> {
    loaded: boolean;
    items: T[];
    uniqueKey: (item: T) => string;
    handleToggle: (value: T) => MouseEventHandler | undefined;
    findIndexById: (arr: T[], item: T) => number;
    checked: T[];
    renderSecondary?: (item: T) => React.ReactNode;
}

const LISTBOX_PADDING = 52; // px
const ITEM_SIZE = 36;

const CustomListItems = <T extends IBaseTransferListObject>({
    loaded,
    items,
    uniqueKey,
    handleToggle,
    findIndexById,
    checked,
    renderSecondary,
}: IOwnProps<T>) => {
    const classes = useStyles();
    const renderNoItemsContainer = () => {
        return (
            items.length === 0 &&
            TranslationHelper.translate(
                'No data to display. Try to change your filters.'
            )
        );
    };

    const getHeight = () => {
        return 6 * ITEM_SIZE;
    };

    const renderRow = ({ data, index, style }: any) => {
        const item = data[index];
        style = { ...style, marginTop: '12px' };
        const key = uniqueKey(item);
        const labelId = `transfer-list-all-item-${key}-label`;
        return (
            <ListItem
                key={key}
                role="listitem"
                button
                onClick={handleToggle(item)}
                style={style}
            >
                <ListItemIcon>
                    <Checkbox
                        checked={findIndexById(checked, item) !== -1}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{
                            'aria-labelledby': labelId,
                        }}
                    />
                </ListItemIcon>
                <Tooltip
                    title={item.name}
                    enterDelay={500}
                    enterNextDelay={500}
                >
                    <ListItemText
                        className={classes.listItemText}
                        classes={{
                            primary: classes.listItemTextPrimary,
                        }}
                        id={labelId}
                        primary={item.name}
                        secondary={renderSecondary?.(item)}
                    />
                </Tooltip>
            </ListItem>
        );
    };

    return items.length > 0 ? (
        <div className={classes.listContainer}>
            <FixedSizeList
                className={classes.list}
                itemData={items}
                height={getHeight() + 2 * LISTBOX_PADDING}
                width="100%"
                itemCount={items.length}
                itemSize={60}
            >
                {renderRow}
            </FixedSizeList>
        </div>
    ) : (
        <div className={classes.noItems}>
            {loaded && renderNoItemsContainer()}
        </div>
    );
};

export default CustomListItems;
